import React, { useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ReferenceLine,
} from 'recharts';
import CustomTooltip from '../../CustomTooltip';
import { colors, lineGridStyle } from '../../../../shared/constants/constants';
import {
    getXAxisLabel,
    getYAxisLabel,
    getChartMargin,
    getXAxisTickFormatter,
    getXAxisDataKey,
    getYAxisDataKey,
    getYAxisTickFormatter,
} from '../../../dashboardUtils';

function GaugeBarGraph({
    chartProperties,
    dataFormatter,
    dataLabels,
    dataPoints,
    firstGroupKey,
    legendFormatter,
    thresholds,
    groupAxisFormatter,
    xAxisLabel,
    yAxisLabelLeft,
}) {
    const [activeIndex, setActiveIndex] = useState(-1);
    const {
        barChartProperties,
        barProperties,
        colorPalette = colors,
        legendProperties,
        referenceLineProperties,
        responsiveContainerProperties,
        showLegend,
        showSinglePointTooltip,
        suffix,
        timeFormat,
        tooltipProperties,
        xAxisProperties,
        yAxisProperties,
    } = chartProperties;

    const isVertical = barChartProperties.layout === 'vertical';
    const xAxisTickFormatter = getXAxisTickFormatter(
        isVertical,
        groupAxisFormatter,
        dataFormatter,
        timeFormat,
        suffix
    );
    const yAxisTickFormatter = getYAxisTickFormatter(
        isVertical,
        groupAxisFormatter,
        dataFormatter,
        timeFormat,
        suffix
    );

    const chartMargin = getChartMargin(
        dataPoints,
        firstGroupKey,
        suffix,
        thresholds,
        timeFormat,
        yAxisTickFormatter,
        yAxisLabelLeft,
        isVertical
    );

    return (
        <ResponsiveContainer {...responsiveContainerProperties}>
            <BarChart {...barChartProperties} data={dataPoints}>
                <CartesianGrid strokeDasharray={lineGridStyle} />
                <XAxis
                    {...xAxisProperties}
                    dataKey={getXAxisDataKey(isVertical, firstGroupKey)}
                    tickFormatter={xAxisTickFormatter}
                    label={getXAxisLabel(xAxisLabel, xAxisProperties)}
                />
                <YAxis
                    {...yAxisProperties}
                    dataKey={getYAxisDataKey(isVertical, firstGroupKey)}
                    label={getYAxisLabel(yAxisLabelLeft)}
                    tickFormatter={yAxisTickFormatter}
                    width={chartMargin.left}
                />
                <Tooltip
                    {...tooltipProperties}
                    cursor={dataPoints.length !== 0}
                    content={
                        <CustomTooltip
                            activeIndex={activeIndex}
                            axisFormatter={
                                isVertical
                                    ? yAxisTickFormatter
                                    : xAxisTickFormatter
                            }
                            axisKey={firstGroupKey}
                            dataFormat={timeFormat}
                            dataFormatter={dataFormatter}
                            legendFormatter={legendFormatter}
                            showSinglePoint={showSinglePointTooltip}
                            suffix={suffix}
                            thresholds={thresholds}
                        />
                    }
                />
                {showLegend && (
                    <Legend {...legendProperties} formatter={legendFormatter} />
                )}
                {dataLabels.map((dataLabel, index) => (
                    <Bar
                        {...barProperties}
                        key={dataLabel}
                        dataKey={dataLabel}
                        fill={colorPalette[index % colorPalette.length]}
                        onMouseOver={() => setActiveIndex(index)}
                        onMouseOut={() => setActiveIndex(-1)}
                        onFocus={() => setActiveIndex(index)}
                        onBlur={() => setActiveIndex(-1)}
                    />
                ))}
                {thresholds.map((threshold) => (
                    <ReferenceLine
                        key={`threshold-${threshold.id}`}
                        {...referenceLineProperties}
                        x={isVertical ? threshold.value : null}
                        y={isVertical ? null : threshold.value}
                        stroke={threshold.lineColor}
                    />
                ))}
            </BarChart>
        </ResponsiveContainer>
    );
}

GaugeBarGraph.propTypes = {
    chartProperties: PropTypes.instanceOf(Object).isRequired,
    dataFormatter: PropTypes.func.isRequired,
    dataLabels: PropTypes.instanceOf(Array),
    dataPoints: PropTypes.instanceOf(Array),
    firstGroupKey: PropTypes.string.isRequired,
    legendFormatter: PropTypes.func.isRequired,
    thresholds: PropTypes.instanceOf(Array).isRequired,
    groupAxisFormatter: PropTypes.func.isRequired,
    xAxisLabel: PropTypes.string,
    yAxisLabelLeft: PropTypes.string,
};

GaugeBarGraph.defaultProps = {
    dataLabels: [],
    dataPoints: [],
    xAxisLabel: null,
    yAxisLabelLeft: null,
};

export default React.memo(
    GaugeBarGraph,
    (prevProps, nextProps) =>
        _.isEqual(prevProps.dataPoints, nextProps.dataPoints) &&
        _.isEqual(prevProps.thresholds, nextProps.thresholds)
);
